import type { AcpPlanData } from '@crowdex/agent-contracts';
import { MessageResponse } from '@crowdex/ui/ai/message';
import { Task, TaskContent, TaskItemFile, TaskTrigger } from '@crowdex/ui/ai/task';
import { FileText, ListChecks } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  SelectionToolbar,
  type TextSelection,
} from '@/components/desktop-side-panel/file-selection-toolbar';
import { fileUrlPath } from '@/lib/desktop-paths';
import type { PendingAnnotation } from '@/lib/desktop-shell/store';
import { selectionWithin, useTouchSelection } from '@/lib/side-panel/text-selection';
import { useSelectionDismiss } from '@/lib/side-panel/use-selection-dismiss';
import { PlanEntry } from './plan-entry';

export function DesktopPlanPanel({
  data,
  onAnnotateSelection,
}: {
  data: AcpPlanData;
  onAnnotateSelection: (selection: PendingAnnotation) => void;
}) {
  const entries = 'entries' in data ? data.entries : [];
  const [selection, setSelection] = useState<TextSelection | null>(null);
  useSelectionDismiss(selection !== null, () => setSelection(null));
  // A finger's selection (its handles, no mouseup) shows the toolbar too.
  const articleRef = useRef<HTMLElement>(null);
  useTouchSelection((found, range) => {
    if (articleRef.current?.contains(range.commonAncestorContainer)) setSelection(found);
  });
  return (
    <article
      className="mx-auto w-full max-w-3xl pb-8"
      onMouseUp={(event) => {
        const found = selectionWithin(event.currentTarget);
        if (found !== null) setSelection(found.selection);
      }}
      ref={articleRef}
    >
      <header className="mb-6 border-hairline border-b pb-5">
        <div className="mb-2 flex items-center gap-2 text-muted-foreground text-xs">
          <ListChecks className="size-3.5" />
          <span>Agent plan</span>
        </div>
        <h1 className="type-section-title text-foreground">Plan</h1>
      </header>
      {'markdown' in data ? (
        <MessageResponse className="text-foreground/95">{data.markdown}</MessageResponse>
      ) : 'uri' in data ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <FileText className="size-4" />
          <span className="truncate">{fileUrlPath(data.uri)}</span>
        </div>
      ) : null}
      {entries.length > 0 && (
        <section className="mt-8 border-hairline border-t pt-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-medium text-foreground text-sm">Tasks</h2>
            <span className="text-muted-foreground text-xs">{entries.length} steps</span>
          </div>
          <ol className="space-y-3">
            {entries.map((entry, index) => (
              <li
                className="rounded-xl border border-hairline bg-card p-3"
                key={entry.id ?? `${index}-${entry.content}`}
              >
                <Task defaultOpen={entry.status === 'in_progress'}>
                  <TaskTrigger title={entry.content}>
                    <div className="w-full cursor-pointer">
                      <PlanEntry entry={entry} />
                    </div>
                  </TaskTrigger>
                  {entry.files !== undefined && entry.files.length > 0 && (
                    <TaskContent>
                      <div className="flex flex-wrap gap-1.5">
                        {entry.files.map((file) => (
                          <TaskItemFile key={file}>{file}</TaskItemFile>
                        ))}
                      </div>
                    </TaskContent>
                  )}
                </Task>
              </li>
            ))}
          </ol>
        </section>
      )}
      {selection !== null && (
        <SelectionToolbar
          annotationLabel="Add feedback"
          ariaLabel="Plan selection actions"
          onAnnotateSelection={onAnnotateSelection}
          onDismiss={() => setSelection(null)}
          selection={selection}
          source={{ sourceId: 'agent-plan', sourceKind: 'plan', sourceLabel: 'Agent plan' }}
        />
      )}
    </article>
  );
}
